/**
 * Phase 1.5 Milestone D — single normalization adapter for the backend's
 * `job.analysis` payload.
 *
 * Backend contract (see backend/app/services/import_csv.py `analyze_bytes`
 * and backend/app/services/imports.py `run_analyze`):
 *
 *   job.analysis = {
 *     encoding: string,
 *     headers: string[],
 *     row_count_data: number,
 *     sample: Array<Record<string, string>>,   // one dict per sample row
 *     warnings: Array<{ code: string; ...extras }>,
 *     suggested_mapping: Record<string, string | null>,
 *     mapping_confirmed?: boolean,
 *   }
 *
 * This adapter is the single source of truth for how the wizard reads that
 * payload. Do not sprinkle `job.analysis.sample` vs `job.analysis.sample_rows`
 * guesses in components; import from here.
 */

export type AnalysisWarning = {
  code: string;
  header?: string;
  expected?: number;
  actual?: number;
  [k: string]: unknown;
};

export type NormalizedAnalysis = {
  headers: string[];
  sampleRows: Array<Record<string, string>>;
  suggestedMapping: Record<string, string | null>;
  rowCountData: number;
  encoding: string | null;
  warnings: AnalysisWarning[];
  hasHeaders: boolean;
};

type RawAnalysis = {
  encoding?: unknown;
  headers?: unknown;
  row_count_data?: unknown;
  sample?: unknown;
  warnings?: unknown;
  suggested_mapping?: unknown;
};

type JobWithAnalysis = { analysis?: RawAnalysis | null };

const EMPTY: NormalizedAnalysis = {
  headers: [],
  sampleRows: [],
  suggestedMapping: {},
  rowCountData: 0,
  encoding: null,
  warnings: [],
  hasHeaders: false,
};

/**
 * Normalize a raw job.analysis payload. Missing/wrong-typed fields fall
 * back to safe defaults so the wizard's Step 3 never crashes on partial
 * data, and the "no headers detected" panel renders instead.
 */
export function normalizeAnalysis(job: JobWithAnalysis | null | undefined): NormalizedAnalysis {
  const raw = job?.analysis;
  if (!raw || typeof raw !== "object") return EMPTY;

  const headers = Array.isArray(raw.headers)
    ? raw.headers.filter((h): h is string => typeof h === "string")
    : [];

  const sampleRows = Array.isArray(raw.sample)
    ? raw.sample.filter(
        (r): r is Record<string, string> =>
          !!r && typeof r === "object" && !Array.isArray(r),
      )
    : [];

  let suggestedMapping: Record<string, string | null> = {};
  if (raw.suggested_mapping && typeof raw.suggested_mapping === "object") {
    for (const [k, v] of Object.entries(raw.suggested_mapping)) {
      if (typeof k !== "string") continue;
      if (v === null || typeof v === "string") suggestedMapping[k] = v;
    }
  }

  const warnings = Array.isArray(raw.warnings)
    ? raw.warnings.filter(
        (w): w is AnalysisWarning =>
          !!w && typeof w === "object" && typeof (w as AnalysisWarning).code === "string",
      )
    : [];

  const rowCountData =
    typeof raw.row_count_data === "number" && Number.isFinite(raw.row_count_data)
      ? raw.row_count_data
      : 0;

  const encoding = typeof raw.encoding === "string" ? raw.encoding : null;

  return {
    headers,
    sampleRows,
    suggestedMapping,
    rowCountData,
    encoding,
    warnings,
    hasHeaders: headers.length > 0,
  };
}
